import { useCallback, useEffect, useState } from 'react';
import { Bug, Loader2, RefreshCw } from 'lucide-react';
import { useAdminAuth } from '../../contexts/AdminAuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';

const fmt = (iso) => new Date(iso).toLocaleString('es-GT', { dateStyle: 'short', timeStyle: 'medium' });

export default function AdminErrorsPage() {
    const { apiCall } = useAdminAuth();
    const [tenants, setTenants] = useState([]);
    const [tenant, setTenant] = useState('all');
    const [source, setSource] = useState('all');
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [expanded, setExpanded] = useState(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const params = new URLSearchParams();
            if (tenant !== 'all') params.set('tenant_id', tenant);
            if (source !== 'all') params.set('source', source);
            setData((await apiCall('get', `/admin/error-log?${params}`)).data);
        } catch (e) {
            setError(e.response?.data?.detail || 'No se pudo cargar el registro de errores');
        }
    }, [apiCall, tenant, source]);

    useEffect(() => { apiCall('get', '/admin/tenants').then((r) => setTenants(r.data.tenants || r.data || [])).catch(() => {}); }, [apiCall]);
    useEffect(() => { load(); }, [load]);

    return (
        <div className="space-y-6" data-testid="admin-errors-page">
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <h1 className="text-4xl font-bold uppercase font-['Barlow_Condensed'] flex items-center gap-3"><Bug className="w-8 h-8 text-amber-500" />Registro de errores</h1>
                    <p className="text-sm text-muted-foreground">Fecha, ruta, aerolínea y usuario de cada error. Sin contraseñas ni tokens. Se borran solos a los {data?.retention_days || 30} días.</p>
                </div>
                <div className="flex gap-2">
                    <Select value={tenant} onValueChange={setTenant}>
                        <SelectTrigger className="w-56" data-testid="errors-tenant-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todas las aerolíneas</SelectItem>
                            <SelectItem value="platform">Plataforma (sin aerolínea)</SelectItem>
                            {[...tenants, ...(data?.tenants || []).filter((x) => !tenants.some((t) => t.id === x.id)).map((x) => ({ ...x, name: `${x.name} (eliminada)` }))].map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                        </SelectContent>
                    </Select>
                    <Select value={source} onValueChange={setSource}>
                        <SelectTrigger className="w-36" data-testid="errors-source-filter"><SelectValue /></SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Todo</SelectItem>
                            <SelectItem value="backend">Servidor</SelectItem>
                            <SelectItem value="frontend">Interfaz</SelectItem>
                        </SelectContent>
                    </Select>
                    <Button variant="outline" size="icon" onClick={load} data-testid="errors-refresh-btn"><RefreshCw className="w-4 h-4" /></Button>
                </div>
            </div>
            <Card>
                <CardHeader><CardTitle className="text-base">{data ? `${data.total} errores` : 'Cargando…'}</CardTitle></CardHeader>
                <CardContent>
                    {error && <p className="text-red-400 text-sm" data-testid="errors-load-error">{error}</p>}
                    {!data && !error && <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>}
                    {data && data.items.length === 0 && <p className="text-sm text-muted-foreground text-center py-10" data-testid="errors-empty">Sin errores registrados con estos filtros.</p>}
                    <div className="divide-y divide-border" data-testid="errors-list">
                        {data?.items.map((e) => (
                            <button key={e.id} className="w-full text-left py-3 text-sm hover:bg-muted/30 px-2 rounded" onClick={() => setExpanded(expanded === e.id ? null : e.id)} data-testid={`error-row-${e.id}`}>
                                <div className="flex flex-wrap items-center gap-2">
                                    <Badge className={e.source === 'frontend' ? 'bg-sky-500/10 text-sky-400' : 'bg-red-500/10 text-red-400'}>{e.source === 'frontend' ? 'Interfaz' : `Servidor ${e.status || ''}`}</Badge>
                                    <span className="font-mono text-xs">{e.method} {e.path}</span>
                                    <span className="ml-auto text-xs text-muted-foreground">{fmt(e.created_at)}</span>
                                </div>
                                <p className="mt-1 text-xs text-muted-foreground">{e.tenant_name || 'Plataforma'} · {e.user_email || 'anónimo'} · {e.error_type}: {e.message}</p>
                                {expanded === e.id && e.trace && <pre className="mt-2 text-[11px] whitespace-pre-wrap bg-muted/30 p-3 rounded max-h-64 overflow-auto">{e.trace}</pre>}
                            </button>
                        ))}
                    </div>
                </CardContent>
            </Card>
        </div>
    );
}
